import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Heart, MessageSquare, Send, Users, 
  ChevronRight, Clock, Smile, Image,
  MoreVertical, ArrowLeft, Crown, Lock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

// 채팅방 목록
const chatRooms = [
  { 
    id: "40s", 
    label: "40대 커플 채팅방", 
    description: "40대 커플들의 대화 공간",
    members: 128,
    color: "from-stone-500 to-stone-700",
    minAge: 40,
    maxAge: 49
  },
  { 
    id: "50s", 
    label: "50대 커플 채팅방", 
    description: "50대 커플들의 대화 공간",
    members: 156,
    color: "from-stone-600 to-stone-800",
    minAge: 50,
    maxAge: 59
  },
  { 
    id: "60s", 
    label: "60대 커플 채팅방", 
    description: "60대 커플들의 대화 공간",
    members: 89,
    color: "from-stone-400 to-stone-600",
    minAge: 60,
    maxAge: 69
  },
  { 
    id: "all", 
    label: "전체 커플 오픈채팅방", 
    description: "모든 연령대가 함께하는 공간",
    members: 342,
    color: "from-stone-600 to-stone-800",
    minAge: 0,
    maxAge: 100
  },
];

// 샘플 메시지 데이터
const sampleMessages = {
  "40s": [
    { id: 1, user: "행복한부부", message: "안녕하세요~ 오늘 날씨가 좋네요!", time: "14:30", isMe: false, avatar: "https://ui-avatars.com/api/?name=행복&background=ec4899&color=fff" },
    { id: 2, user: "달콤커플", message: "네 정말요! 주말에 나들이 가셨나요?", time: "14:32", isMe: false, avatar: "https://ui-avatars.com/api/?name=달콤&background=f472b6&color=fff" },
    { id: 3, user: "나", message: "저희는 한강에 다녀왔어요 ㅎㅎ", time: "14:35", isMe: true },
    { id: 4, user: "사랑스러운", message: "와 좋겠다~ 저희도 다음주에 가려구요", time: "14:36", isMe: false, avatar: "https://ui-avatars.com/api/?name=사랑&background=db2777&color=fff" },
    { id: 5, user: "행복한부부", message: "한강 어느 쪽이 좋아요?", time: "14:38", isMe: false, avatar: "https://ui-avatars.com/api/?name=행복&background=ec4899&color=fff" },
  ],
  "50s": [
    { id: 1, user: "황혼커플", message: "다들 좋은 하루 보내고 계신가요?", time: "13:20", isMe: false, avatar: "https://ui-avatars.com/api/?name=황혼&background=8b5cf6&color=fff" },
    { id: 2, user: "행복여행", message: "네~ 오늘 등산 다녀왔어요!", time: "13:25", isMe: false, avatar: "https://ui-avatars.com/api/?name=행복&background=7c3aed&color=fff" },
    { id: 3, user: "나", message: "등산 어디로 가셨어요?", time: "13:28", isMe: true },
  ],
  "60s": [
    { id: 1, user: "은빛사랑", message: "좋은 오후입니다~", time: "15:00", isMe: false, avatar: "https://ui-avatars.com/api/?name=은빛&background=3b82f6&color=fff" },
    { id: 2, user: "나", message: "안녕하세요! 반갑습니다", time: "15:05", isMe: true },
  ],
  "all": [
    { id: 1, user: "New Coupling", message: "환영합니다! 커플 오픈채팅방입니다 💕", time: "12:00", isMe: false, isSystem: true },
    { id: 2, user: "새로운시작", message: "안녕하세요~ 처음 왔어요!", time: "12:30", isMe: false, avatar: "https://ui-avatars.com/api/?name=새로운&background=7c2d12&color=fff" },
    { id: 3, user: "행복전도사", message: "환영해요! 여기 분위기 좋아요~", time: "12:32", isMe: false, avatar: "https://ui-avatars.com/api/?name=행복&background=991b1b&color=fff" },
    { id: 4, user: "나", message: "반갑습니다 여러분!", time: "12:35", isMe: true },
  ],
};

// 온라인 사용자 데이터
const onlineUsers = {
  "40s": [
    { name: "행복한부부", avatar: "행복" },
    { name: "달콤커플", avatar: "달콤" },
    { name: "사랑스러운", avatar: "사랑" },
    { name: "봄날의연인", avatar: "봄날" },
    { name: "함께걷는길", avatar: "함께" },
  ],
  "50s": [
    { name: "황혼커플", avatar: "황혼" },
    { name: "행복여행", avatar: "행복" },
    { name: "가을연가", avatar: "가을" },
    { name: "따뜻한오후", avatar: "따뜻" },
  ],
  "60s": [
    { name: "은빛사랑", avatar: "은빛" },
    { name: "석양커플", avatar: "석양" },
    { name: "노을빛사랑", avatar: "노을" },
  ],
  "all": [
    { name: "새로운시작", avatar: "새로" },
    { name: "행복전도사", avatar: "행복" },
    { name: "봄날의햇살", avatar: "봄날" },
    { name: "설레는마음", avatar: "설레" },
    { name: "진심으로", avatar: "진심" },
    { name: "두번째봄", avatar: "두번" },
  ],
};

export default function CoupleChatPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [searchParams] = useSearchParams();
  const roomParam = searchParams.get("room");
  
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [showRoomList, setShowRoomList] = useState(!roomParam);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (roomParam) {
      const room = chatRooms.find(r => r.id === roomParam);
      if (room) {
        setSelectedRoom(room);
        setMessages(sampleMessages[roomParam] || []);
        setShowRoomList(false);
      }
    }
  }, [roomParam]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleRoomSelect = (room) => {
    if (!isAuthenticated) {
      alert("채팅방에 입장하려면 로그인이 필요합니다.");
      return;
    }
    setSelectedRoom(room);
    setMessages(sampleMessages[room.id] || []);
    setShowRoomList(false);
    navigate(`/couple-chat?room=${room.id}`);
  };

  const handleSendMessage = () => {
    if (!newMessage.trim() || !isAuthenticated) return;
    
    const newMsg = {
      id: messages.length + 1,
      user: "나",
      message: newMessage,
      time: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
      isMe: true,
    };
    
    setMessages([...messages, newMsg]);
    setNewMessage("");
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleBackToList = () => {
    setSelectedRoom(null);
    setShowRoomList(true);
    navigate('/couple-chat');
  };

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Hero Section */}
      <section className="relative bg-stone-100 py-12 border-b border-stone-200">
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-stone-200 rounded-full mb-4">
              <MessageSquare className="w-4 h-4 text-burgundy-500" />
              <span className="text-stone-600 text-sm">NC커플즈</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-3">
              커플 채팅방
            </h1>
            <p className="text-stone-500">
              같은 연령대의 커플들과 실시간으로 소통해보세요
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {showRoomList ? (
            /* Room List View */
            <motion.div
              key="room-list"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {chatRooms.map((room) => (
                  <motion.div
                    key={room.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => handleRoomSelect(room)}
                  >
                    <div className={`h-24 bg-gradient-to-r ${room.color} relative`}>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <MessageSquare className="w-12 h-12 text-white/30" />
                      </div>
                      {room.id === "all" && (
                        <div className="absolute top-3 right-3 px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                          <span className="text-white text-xs font-medium">OPEN</span>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-lg text-stone-800 mb-1">{room.label}</h3>
                      <p className="text-stone-500 text-sm mb-4">{room.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-stone-400 text-sm">
                          <Users className="w-4 h-4" />
                          <span>{room.members}명 참여중</span>
                        </div>
                        <Button
                          size="sm"
                          className={`bg-gradient-to-r ${room.color} text-white border-0`}
                        >
                          입장하기
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Notice */}
              <div className="mt-8 p-4 bg-stone-50 border border-stone-100 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-stone-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Heart className="w-4 h-4 text-stone-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-stone-900 mb-1">채팅방 이용 안내</h4>
                    <ul className="text-sm text-stone-700 space-y-1">
                      <li>• 상대방을 존중하는 대화를 부탁드립니다</li>
                      <li>• 개인정보 공유는 신중하게 해주세요</li>
                      <li>• 부적절한 내용은 신고해 주세요</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            /* Chat Room View */
            <motion.div
              key="chat-room"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden"
            >
              {/* Chat Header */}
              <div className={`bg-gradient-to-r ${selectedRoom?.color} p-4`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleBackToList}
                      className="p-2 hover:bg-white/20 rounded-full transition-colors"
                    >
                      <ArrowLeft className="w-5 h-5 text-white" />
                    </button>
                    <div>
                      <h2 className="font-bold text-white">{selectedRoom?.label}</h2>
                      <p className="text-white/70 text-sm flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {selectedRoom?.members}명 참여중
                      </p>
                    </div>
                  </div>
                  <button className="p-2 hover:bg-white/20 rounded-full transition-colors">
                    <MoreVertical className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Chat Body: Online Users + Messages */}
              <div className="flex">
                {/* Online Users Sidebar */}
                <div className="w-48 border-r border-stone-200 bg-white hidden md:block">
                  <div className="p-3 border-b border-stone-100">
                    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">접속 중</p>
                  </div>
                  <div className="overflow-y-auto h-[400px] p-2 space-y-1">
                    {(onlineUsers[selectedRoom?.id] || []).map((u, i) => (
                      <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-stone-50">
                        <div className="relative">
                          <div className="w-7 h-7 rounded-full bg-stone-200 flex items-center justify-center text-xs font-medium text-stone-600">
                            {u.avatar.slice(0, 1)}
                          </div>
                          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
                        </div>
                        <span className="text-sm text-stone-700 truncate">{u.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Messages Area */}
                <div className="flex-1">
                  <div className="h-[400px] overflow-y-auto p-4 bg-stone-50">
                {!isAuthenticated ? (
                  <div className="h-full flex flex-col items-center justify-center">
                    <Lock className="w-12 h-12 text-stone-300 mb-4" />
                    <p className="text-stone-500 mb-4">로그인 후 채팅에 참여할 수 있습니다</p>
                    <Button
                      className="bg-stone-600 hover:bg-stone-700"
                      onClick={() => navigate('/')}
                    >
                      로그인하기
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.isMe ? 'justify-end' : 'justify-start'}`}
                      >
                        {msg.isSystem ? (
                          <div className="w-full text-center">
                            <span className="inline-block px-4 py-2 bg-stone-100 text-stone-700 rounded-full text-sm">
                              {msg.message}
                            </span>
                          </div>
                        ) : (
                          <div className={`flex items-end gap-2 max-w-[70%] ${msg.isMe ? 'flex-row-reverse' : ''}`}>
                            {!msg.isMe && (
                              <img
                                src={msg.avatar}
                                alt={msg.user}
                                className="w-8 h-8 rounded-full"
                              />
                            )}
                            <div>
                              {!msg.isMe && (
                                <p className="text-xs text-stone-500 mb-1 ml-1">{msg.user}</p>
                              )}
                              <div
                                className={`px-4 py-2 rounded-2xl ${
                                  msg.isMe
                                    ? 'bg-stone-600 text-white rounded-br-sm'
                                    : 'bg-white text-stone-800 rounded-bl-sm shadow-sm'
                                }`}
                              >
                                <p className="text-sm">{msg.message}</p>
                              </div>
                              <p className={`text-xs text-stone-400 mt-1 ${msg.isMe ? 'text-right mr-1' : 'ml-1'}`}>
                                {msg.time}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>
                </div>
              </div>

              {/* Message Input */}
              {isAuthenticated && (
                <div className="p-4 border-t border-stone-200 bg-white">
                  <div className="flex items-center gap-3">
                    <button className="p-2 text-stone-400 hover:text-stone-600 transition-colors">
                      <Image className="w-5 h-5" />
                    </button>
                    <button className="p-2 text-stone-400 hover:text-stone-600 transition-colors">
                      <Smile className="w-5 h-5" />
                    </button>
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="메시지를 입력하세요..."
                      className="flex-1 px-4 py-2 bg-stone-100 rounded-full focus:outline-none focus:ring-2 focus:ring-stone-600/20"
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      className="bg-stone-600 hover:bg-stone-700 rounded-full w-10 h-10 p-0"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
